import { useNavigate } from 'react-router-dom';
import type { InspectionQueueItem } from '@/services/dashboard.service';
import { ROUTES } from '@/routes/routes';

const STATUS_BADGE: Record<string, string> = {
  SCHEDULED: 'bg-sky-500/10 text-sky-700',
  ASSIGNED: 'bg-blue-500/10 text-blue-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
  REVIEWED: 'bg-purple-500/10 text-purple-700',
  CLOSED: 'bg-green-500/10 text-green-700',
  CANCELLED: 'bg-red-500/10 text-red-700',
};

const STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

interface QueueTableProps {
  title: string;
  items: InspectionQueueItem[];
  emptyMessage?: string;
  showEngineer?: boolean;
  /**
   * Optional "View all" destination rendered as a footer link.
   *
   * OPT-IN and undefined by default, so every existing consumer (Admin,
   * Commercial, Operations, Owner) renders byte-identically. The dashboard
   * queue is a deliberate server-side top-N subset, so the honest affordance is
   * to hand the operator off to the full paginated list rather than paginate a
   * summary.
   */
  viewAllTo?: string;
  /** Label for the footer link. Ignored unless `viewAllTo` is set. */
  viewAllLabel?: string;
}

export function QueueTable({
  title,
  items,
  emptyMessage = 'No items in this queue.',
  showEngineer = true,
  viewAllTo,
  viewAllLabel = 'View all',
}: QueueTableProps) {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground text-center">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="px-5 py-2.5">Inspection #</th>
                <th className="px-5 py-2.5">Customer</th>
                <th className="px-5 py-2.5 hidden sm:table-cell">Equipment</th>
                {showEngineer && <th className="px-5 py-2.5 hidden md:table-cell">Engineer</th>}
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 hidden lg:table-cell">Scheduled</th>
                <th className="px-5 py-2.5 text-right hidden lg:table-cell">Findings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="cursor-pointer transition-colors hover:bg-muted/30"
                  onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', item.id))}
                >
                  <td className="px-5 py-3 font-medium text-primary">{item.inspectionNumber}</td>
                  <td className="px-5 py-3 text-foreground">
                    {item.customerOrganizationName ?? item.customerName ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden sm:table-cell">
                    {item.equipmentName ?? '—'}
                  </td>
                  {showEngineer && (
                    <td className="px-5 py-3 text-muted-foreground hidden md:table-cell">
                      {item.engineerName ?? '—'}
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[item.status] ?? 'bg-muted text-muted-foreground'}`}
                    >
                      {STATUS_LABEL[item.status] ?? item.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden lg:table-cell">
                    {fmtDate(item.scheduledAt)}
                  </td>
                  <td className="px-5 py-3 text-right text-muted-foreground hidden lg:table-cell">
                    {item.totalFindingsCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewAllTo && (
        <div className="border-t border-border px-5 py-2.5 text-right">
          <button
            type="button"
            onClick={() => navigate(viewAllTo)}
            data-testid="queue-view-all"
            className="text-xs font-medium text-primary transition-colors hover:underline"
          >
            {viewAllLabel} &rarr;
          </button>
        </div>
      )}
    </div>
  );
}

export function QueueTableSkeleton({ title }: { title: string }) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-3.5">
            <div className="h-4 w-24 rounded skeleton-shimmer" />
            <div className="h-4 w-32 rounded skeleton-shimmer" />
            <div className="h-4 w-24 rounded skeleton-shimmer hidden sm:block" />
            <div className="h-5 w-16 rounded-full skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  );
}
